import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, Search, Edit, Trash2, Shield, RefreshCw, Eye, Filter, Download,
  CheckSquare, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { rolesAPI } from '../../services/api';

const roleSchema = z.object({
  role_name: z.string().min(1, 'Role name is required'),
  description: z.string().min(1, 'Description is required'),
  permissions: z.array(z.string()).min(1, 'At least one permission is required'),
  is_active: z.boolean().default(true),
});

const availablePermissions = [
  // User Management
  'user:read', 'user:create', 'user:update', 'user:delete',
  // Role Management
  'role:view', 'role:manage',
  // Branch Management
  'branch:read', 'branch:create', 'branch:update', 'branch:delete',
  // School Management
  'school:read', 'school:create', 'school:update', 'school:delete',
  // Attendance Management
  'attendance:read', 'attendance:clockin', 'attendance:clockout', 'attendance:view',
  // Staff Attendance
  'staff_attendance:read', 'staff_attendance:manage',
  // Meal Management
  'meal:request:read', 'meal:request:create', 'meal:request:update', 'meal:request:delete', 'meal:served:read', 'meal:served:update',
  // Calendar Management
  'calendar:read', 'calendar:view', 'calendar:manage',
  // Task Management
  'task:read', 'task:create', 'task:update', 'task:delete',
  // Leave Management
  'leave:request:read', 'leave:request:create', 'leave:request:update', 'leave:request:approve', 'leave:request:delete',
  // Report Management
  'report:view', 'report:generate',
  // Settings Management
  'settings:read', 'settings:update',
  // Dashboard
  'dashboard:view',
  // Activities
  'activity:view',
];

const permissionCategories = {
  'User Management': ['user:read', 'user:create', 'user:update', 'user:delete'],
  'Role Management': ['role:view', 'role:manage'],
  'Branch Management': ['branch:read', 'branch:create', 'branch:update', 'branch:delete'],
  'School Management': ['school:read', 'school:create', 'school:update', 'school:delete'],
  'Attendance': ['attendance:read', 'attendance:clockin', 'attendance:clockout', 'attendance:view'],
  'Staff Attendance': ['staff_attendance:read', 'staff_attendance:manage'],
  'Meal Management': ['meal:request:read', 'meal:request:create', 'meal:request:update', 'meal:request:delete', 'meal:served:read', 'meal:served:update'],
  'Calendar': ['calendar:read', 'calendar:view', 'calendar:manage'],
  'Tasks': ['task:read', 'task:create', 'task:update', 'task:delete'],
  'Leave': ['leave:request:read', 'leave:request:create', 'leave:request:update', 'leave:request:approve', 'leave:request:delete'],
  'Reports': ['report:view', 'report:generate'],
  'Settings': ['settings:read', 'settings:update'],
  'Dashboard': ['dashboard:view'],
  'Activities': ['activity:view'],
};

const RolesManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [viewingRole, setViewingRole] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, roleId: null, roleName: '' });
  const hasLoaded = useRef(false);

  const defaultValues = {
    role_name: '',
    description: '',
    permissions: [],
    is_active: true,
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(roleSchema),
    defaultValues,
  });

  const selectedPermissions = watch('permissions');

  useEffect(() => {
    if (hasLoaded.current) return;
    loadRoles();
    hasLoaded.current = true;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getRoles();
      // Handle different response structures
      const rolesArray = Array.isArray(response) ? response : response.roles || [];
      setRoles(rolesArray);
    } catch (error) {
      console.error('Failed to load roles:', error);
      toast.error('Failed to load roles', 'Please try again');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadRoles();
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const roleData = {
        role_name: data.role_name,
        description: data.description,
        permissions: data.permissions,
        is_active: data.is_active,
      };

      if (editingRole) {
        await rolesAPI.updateRole(editingRole.id, roleData);
        toast.success('Role updated successfully!');
      } else {
        await rolesAPI.createRole(roleData);
        toast.success('Role created successfully!');
      }
      
      setShowForm(false);
      setEditingRole(null);
      reset();
      await loadRoles();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save role';
      toast.error('Failed to save role', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    reset({
      role_name: role.role_name,
      description: role.description,
      permissions: role.permissions || [],
      is_active: role.is_active,
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingRole(null);
    reset(defaultValues);
    setShowForm(true);
  };

  const handleDelete = (role) => {
    setDeleteConfirm({ show: true, roleId: role.id, roleName: role.role_name });
  };

  const confirmDelete = async () => {
    try {
      await rolesAPI.deleteRole(deleteConfirm.roleId);
      toast.success('Role deleted successfully!');
      await loadRoles();
      setDeleteConfirm({ show: false, roleId: null, roleName: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete role';
      toast.error('Failed to delete role', errorMessage);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, roleId: null, roleName: '' });
  };

  const handleView = (role) => {
    setViewingRole(role);
  };

  const handlePermissionToggle = (permission) => {
    const currentPermissions = selectedPermissions || [];
    if (currentPermissions.includes(permission)) {
      setValue('permissions', currentPermissions.filter(p => p !== permission));
    } else {
      setValue('permissions', [...currentPermissions, permission]);
    }
  };

  const handleCategoryToggle = (categoryPermissions) => {
    const currentPermissions = selectedPermissions || [];
    const allSelected = categoryPermissions.every(p => currentPermissions.includes(p));
    
    if (allSelected) {
      // Remove all permissions in this category
      const newPermissions = currentPermissions.filter(p => !categoryPermissions.includes(p));
      setValue('permissions', newPermissions);
    } else {
      // Add all permissions in this category
      const newPermissions = [...new Set([...currentPermissions, ...categoryPermissions])];
      setValue('permissions', newPermissions);
    }
  };

  const getRoleColor = (roleName) => {
    switch (roleName) {
      case 'super_admin': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'ffln_admin': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'branch_manager': return 'bg-green-100 text-green-700 border-green-200';
      case 'school_admin': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredRoles = roles.filter(role =>
    role.role_name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  const roleStats = {
    total: roles.length,
    active: roles.filter(r => r.is_active).length,
    inactive: roles.filter(r => !r.is_active).length,
    adminRoles: roles.filter(r => ['super_admin', 'ffln_admin'].includes(r.role_name)).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Roles Management</h1>
            <p className="text-gray-600 mt-2">Manage user roles and permissions</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              Add Role
            </Button>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Roles', value: roleStats.total, color: 'blue' },
          { label: 'Active Roles', value: roleStats.active, color: 'green' },
          { label: 'Inactive Roles', value: roleStats.inactive, color: 'red' },
          { label: 'Admin Roles', value: roleStats.adminRoles, color: 'purple' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-4">
                <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Role Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Card>
              <CardHeader>
                <CardTitle>{editingRole ? 'Edit Role' : 'Add New Role'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="role_name">Role Name</Label>
                    <Controller
                      name="role_name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter role name"
                          {...field}
                          error={errors.role_name?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          placeholder="Enter role description"
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="is_active">Status</Label>
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value={true}>Active</option>
                          <option value={false}>Inactive</option>
                        </Select>
                      )}
                    />
                  </div>

                  <div>
                    <Label>Permissions</Label>
                    <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {Object.entries(permissionCategories).map(([category, permissions]) => (
                        <div key={category} className="border-b border-gray-100 pb-2 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">{category}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCategoryToggle(permissions)}
                              className="text-xs"
                            >
                              {permissions.every(p => selectedPermissions?.includes(p)) ? 'Deselect All' : 'Select All'}
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            {permissions.map(permission => (
                              <label key={permission} className="flex items-center text-xs">
                                <input
                                  type="checkbox"
                                  checked={selectedPermissions?.includes(permission) || false}
                                  onChange={() => handlePermissionToggle(permission)}
                                  className="mr-1 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                                />
                                {permission.replace(/_/g, ' ')}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.permissions && (
                      <p className="text-red-500 text-xs mt-1">{errors.permissions.message}</p>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      {editingRole ? 'Update' : 'Create'} Role
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingRole(null);
                        reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Roles List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${showForm ? 'lg:col-span-2' : 'lg:col-span-3'}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Roles</CardTitle>
              <div className="flex items-center space-x-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Button variant="outline" size="sm">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredRoles.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No roles found</p>
                  </div>
                ) : (
                  filteredRoles.map((role, index) => (
                    <motion.div
                      key={role.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                      className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{role.role_name}</h3>
                            <Badge className={getRoleColor(role.role_name)}>
                              {role.role_name}
                            </Badge>
                            <Badge variant={role.is_active ? 'success' : 'error'}>
                              {role.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2">{role.description}</p>
                          
                          <div className="flex flex-wrap gap-1">
                            {role.permissions?.slice(0, 5).map((permission, idx) => (
                              <Badge key={idx} variant="info" className="text-xs">
                                {permission.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                            {role.permissions?.length > 5 && (
                              <Badge variant="info" className="text-xs">
                                +{role.permissions.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <Button size="sm" variant="ghost" onClick={() => handleView(role)}>
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(role)}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(role)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Role Details Modal */}
      {viewingRole && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewingRole(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{viewingRole.role_name}</h2>
              <Button variant="ghost" size="sm" onClick={() => setViewingRole(null)}>
                ×
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="font-medium">{viewingRole.description}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={viewingRole.is_active ? 'success' : 'error'}>
                  {viewingRole.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">Permissions</p>
                <div className="grid grid-cols-2 gap-2">
                  {viewingRole.permissions?.map((permission, index) => (
                    <Badge key={index} variant="info" className="text-xs">
                      {permission.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-red-600 mr-3" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Delete Role</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete the role <strong>"{deleteConfirm.roleName}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will remove the role from all users who have it assigned.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete Role
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default RolesManagement;
